// src/pages/dashboard/Dashboard.jsx
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  Users,
  FileText,
  Settings,
  CheckCircle,
  Clock,
  AlertTriangle,
  Calendar,
  BarChart3,
  Activity,
  FileStack,
} from 'lucide-react';
import toast from 'react-hot-toast';

// Import components
import StatisticsCard from './components/StatisticsCard';
import RecentActivity from './components/RecentActivity';
import ContractsEvolutionChart from './components/ContractsEvolutionChart';

// Import API service
import { dashboardService } from '../../services/dashboardService';

const Dashboard = () => {
  // Fetch dashboard statistics
  const {
    data: statistics,
    isLoading: statisticsLoading,
    error: statisticsError,
    refetch: refetchStatistics
  } = useQuery('dashboardStatistics', dashboardService.getStatistics, {
    refetchInterval: 300000, // Refresh every 5 minutes
    retry: 2,
    onError: (error) => {
      console.error('Error fetching statistics:', error);
      toast.error('Eroare la încărcarea statisticilor');
    }
  });

  // Fetch recent activity
  const {
    data: recentActivity,
    isLoading: activityLoading,
    error: activityError,
    refetch: refetchActivity
  } = useQuery('recentActivity', dashboardService.getRecentActivity, {
    refetchInterval: 120000, // Refresh every 2 minutes
    retry: 2,
    onError: (error) => {
      console.error('Error fetching recent activity:', error);
      toast.error('Eroare la încărcarea activității recente');
    }
  });

  const handleRefreshAll = () => {
    refetchStatistics();
    refetchActivity();
    console.log(recentActivity);
    toast.success('Date actualizate');
    console.log(
      "statistics:", statistics);
  };

  // Statistics cards configuration - NO HARDCODED TREND VALUES
  const statisticsCards = statistics ? [
    {
      title: 'Total Clienți',
      value: statistics.totalClients || 0,
      icon: Users,
      color: 'primary'
    },
    {
      title: 'Contracte Active',
      value: statistics.activeContracts || 0,
      icon: FileText,
      color: 'success'
    },
    {
      title: 'Contracte Semnate',
      value: statistics.signedContracts || 0,
      icon: CheckCircle,
      color: 'info'
    },
    {
      title: 'Contracte în Așteptare',
      value: statistics.pendingContracts || 0,
      icon: Clock,
      color: 'warning'
    },
    {
      title: 'Contracte care Expiră',
      value: statistics.expiringContracts || 0,
      icon: AlertTriangle,
      color: 'danger'
    },
    {
      title: 'Total Servicii',
      value: statistics.totalServices || 0,
      icon: Settings,
      color: 'secondary'
    }
  ] : [];

  const quickActions = [
    {
      title: 'Contract Nou',
      description: 'Creează un contract',
      icon: FileText,
      color: 'text-blue-500',
      href: '/contracts'
    },
    {
      title: 'Client Nou',
      description: 'Adaugă un client',
      icon: Users,
      color: 'text-green-500',
      href: '/clients'
    },
    {
      title: 'Serviciu Nou',
      description: 'Adaugă un serviciu',
      icon: Settings,
      color: 'text-purple-500',
      href: '/services'
    },
    {
      title: 'Bulk Contracte',
      description: 'Creează contracte bulk',
      icon: FileStack,
      color: 'text-yellow-500',
      href: '/bulk-contracts'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Privire de ansamblu asupra sistemului de management contracte
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center text-sm text-gray-500">
                  <Calendar className="w-4 h-4 mr-2" />
                  {new Date().toLocaleDateString('ro-RO', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
                <button
                  onClick={handleRefreshAll}
                  disabled={statisticsLoading || activityLoading}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Activity className="w-4 h-4 mr-2" />
                  {statisticsLoading || activityLoading ? 'Se încarcă...' : 'Actualizează'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Banner */}
        {(statisticsError || activityError) && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
              <div>
                <p className="text-sm text-red-700 font-medium">
                  A apărut o eroare la încărcarea datelor
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {statisticsError?.message || activityError?.message ||
                    'Verificați conexiunea la server și reîncărcați pagina.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards Grid - NO TREND DATA */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
          {statisticsLoading ? (
            // Loading skeleton
            [...Array(6)].map((_, index) => (
              <div key={index} className="col-span-1">
                <StatisticsCard
                  title="Loading..."
                  value={0}
                  icon={Users}
                  color="primary"
                  isLoading={true}
                />
              </div>
            ))
          ) : statistics ? (
            statisticsCards.map((card, index) => (
              <div key={index} className="col-span-1">
                <StatisticsCard
                  title={card.title}
                  value={card.value}
                  icon={card.icon}
                  color={card.color}
                  isLoading={false}
                />
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-8">
              <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nu s-au putut încărca statisticile</p>
            </div>
          )}
        </div>

        {/* Charts and Recent Activity Row */}
        <div className="mb-8">
          {/* Quick Stats Chart - NO HARDCODED PERCENTAGE */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Statistici Contracte
                </h3>
              </div>

              <ContractsEvolutionChart
                statistics={statistics}
                isLoading={statisticsLoading}
              />


              {/* Quick metrics below chart - REAL DATA ONLY */}
              {statistics && (
                <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {statistics.signedContracts}
                    </div>
                    <div className="text-sm text-gray-500">Semnate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {statistics.pendingContracts}
                    </div>
                    <div className="text-sm text-gray-500">În așteptare</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {statistics.expiringContracts}
                    </div>
                    <div className="text-sm text-gray-500">Expiră curând</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Alerts and Additional Info Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Activity */}
          <div className="lg:col-span-1">
            <RecentActivity
              activities={recentActivity || []}
              isLoading={activityLoading}
            />
          </div>
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Acțiuni Rapide
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-left group"
                    onClick={() => {
                      window.location.href = action.href;
                      toast.success(`Navigare către ${action.title}`);
                    }}
                  >
                    <action.icon className={`w-6 h-6 ${action.color} mb-2 group-hover:scale-110 transition-transform`} />
                    <div className="text-sm font-medium text-gray-900">{action.title}</div>
                    <div className="text-xs text-gray-500">{action.description}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;